/**
 * Light, dark or the system's choice — remembered per browser.
 *
 * The resolved theme is the `data-theme` attribute on <html>, which the
 * `dark:` variant and the semantic colour tokens in index.css follow.
 * `public/theme-init.js` applies the saved choice before the first paint;
 * this hook owns it afterwards, and in "system" mode follows the OS live.
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react'

export type ThemeMode = 'light' | 'dark' | 'system'
export type Theme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'hkcm-theme'

const DARK_QUERY = '(prefers-color-scheme: dark)'
const listeners = new Set<() => void>()

function readMode(): ThemeMode {
    try {
        const saved = window.localStorage.getItem(THEME_STORAGE_KEY)
        return saved === 'light' || saved === 'dark' ? saved : 'system'
    } catch {
        return 'system'
    }
}

function systemPrefersDark(): boolean {
    return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches
}

export function resolveTheme(mode: ThemeMode): Theme {
    if (mode === 'system') return systemPrefersDark() ? 'dark' : 'light'
    return mode
}

function apply(mode: ThemeMode): void {
    document.documentElement.setAttribute('data-theme', resolveTheme(mode))
}

function emit(): void {
    for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
}

export function setThemeMode(mode: ThemeMode): void {
    try {
        if (mode === 'system') window.localStorage.removeItem(THEME_STORAGE_KEY)
        else window.localStorage.setItem(THEME_STORAGE_KEY, mode)
    } catch {
        // Storage blocked: the choice lasts until the tab closes.
    }
    apply(mode)
    emit()
}

export function useTheme(): { mode: ThemeMode; theme: Theme; setMode: (mode: ThemeMode) => void } {
    const mode = useSyncExternalStore(subscribe, readMode, () => 'system' as ThemeMode)

    // In "system" mode, follow the OS when it switches (e.g. at sunset).
    useEffect(() => {
        if (mode !== 'system' || typeof window.matchMedia !== 'function') return
        const query = window.matchMedia(DARK_QUERY)
        const onChange = () => {
            apply('system')
            emit()
        }
        query.addEventListener('change', onChange)
        return () => query.removeEventListener('change', onChange)
    }, [mode])

    const setMode = useCallback((next: ThemeMode) => setThemeMode(next), [])
    return { mode, theme: resolveTheme(mode), setMode }
}
